import { UIRouterReact } from '@uirouter/react';
import type { FeatureName } from '@logz-pkg/analytics';
import { analyticsService } from 'services/analytics/analytics.service';

type DashboardFeature = Extract<FeatureName, 'Grafana' | 'OpenSearch Dashboards' | 'Unified Dashboards'>;

interface IDashboardStateConfig {
  feature: DashboardFeature;
  getDashboardId: (params: Record<string, string | undefined>) => string | undefined;
}

const NEW_DASHBOARD_ID = 'new';

const DASHBOARD_STATES = new Map<string, IDashboardStateConfig>([
  [
    'dashboard.metrics',
    {
      feature: 'Grafana',
      // Only `d/{uid}` paths are dashboards — list / alerting / explore metrics routes must not emit an open.
      getDashboardId: params =>
        params.uid || (params.resource?.startsWith('d/') ? params.resource.split('/')[1] : undefined),
    },
  ],
  ['dashboard.osd', { feature: 'OpenSearch Dashboards', getDashboardId: params => params.resource?.split('view/')[1] }],
  ['dashboard.view-dashboard', { feature: 'Unified Dashboards', getDashboardId: params => params.dashboardId }],
]);

/**
 * Fires a single `Opened dashboard` analytics event when a dashboard is opened in the legacy app,
 * deduped per dashboard id (reset on leaving), mirroring app-ui's tracking. Covers Grafana
 * (`dashboard.metrics`), OpenSearch (`dashboard.osd`) and Unified (`dashboard.view-dashboard`).
 */
export const registerDashboardOpenAnalytics = (appRouter: UIRouterReact): void => {
  let lastTrackedDashboardId: string | undefined;

  appRouter.transitionService.onSuccess({}, transition => {
    const config = DASHBOARD_STATES.get(transition.to().name ?? '');
    const dashboardId = config?.getDashboardId(transition.params());

    // Not on a specific dashboard (other route, a list / non-view page, or the create flow) —
    // clear the dedupe so re-opening the same dashboard afterwards fires again.
    if (!config || !dashboardId || dashboardId === NEW_DASHBOARD_ID) {
      lastTrackedDashboardId = undefined;

      return;
    }

    if (dashboardId === lastTrackedDashboardId) return;

    lastTrackedDashboardId = dashboardId;
    analyticsService.capture(config.feature, 'Opened dashboard', { dashboardId });
  });
};
